import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ago } from '../../components/three/ecosystemState';
import { apiGet, apiPost } from '../../utils/backendApi';
import { appLive } from './routes';
import { LiveSection } from './LiveFrame';
import { RunChart, RunLegend } from './charts';
import BehaviorBaseline from './BehaviorBaseline';

const SEV = ['critical', 'high', 'medium', 'low', 'info'];
const SCANNERS = [
  { key: 'zap-baseline', name: 'ZAP', test: 'Passive web-app scan. It reads responses and does not send attack payloads. Active mode is operator-only.' },
  { key: 'nuclei', name: 'Nuclei', test: 'Known-CVE templates against the public URL.' },
  { key: 'testssl', name: 'TLS', test: 'Certificate, protocol, and cipher configuration.' },
  { key: 'image', name: 'Image (SCA)', test: 'OS packages and npm dependencies inside the running container image, read hourly from the registry scan.', hourly: true },
];
const imageShort = (ref) => (ref ? String(ref).replace(/^.*\//, '').replace(/@sha256:(\w{12}).*$/, '@$1') : '');
const SCANNER_LABEL = Object.fromEntries(SCANNERS.map((s) => [s.key, s.name]));

function stageOf(job, key) {
  return (job?.stages || []).find((s) => (s.name || s.scanner) === key) || null;
}

export default function SecurityView({ orgId, appId, isAdmin, telemetry, controls, slug = null }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [checks, setChecks] = useState(null);
  const [status, setStatus] = useState('open');
  const [sev, setSev] = useState('all');
  const [scanner, setScanner] = useState('all');

  useEffect(() => {
    let alive = true;
    const pull = () => apiGet(`/apps/${orgId}/${appId}/security`)
      .then((d) => { if (alive) { setData(d); setErr(''); } })
      .catch((e) => { if (alive) setErr(e.message); });
    pull();
    const t = setInterval(pull, 30_000);
    apiGet(`/apps/${orgId}/${appId}/health`)
      .then((d) => { if (alive) setChecks(d.features || []); })
      .catch(() => { if (alive) setChecks([]); });
    return () => { alive = false; clearInterval(t); };
  }, [orgId, appId]);

  const runScan = async () => {
    setBusy(true); setMsg('');
    try {
      const out = await apiPost(`/apps/${orgId}/${appId}/scan`);
      setMsg(`Baseline queued (${out.jobId}). Each scanner reports as it finishes — usually 5–15 minutes.`);
      setData(await apiGet(`/apps/${orgId}/${appId}/security`));
    } catch (e) { setMsg(e.message); } finally { setBusy(false); }
  };

  const findings = data?.findings || [];
  const jobs = data?.jobs || [];
  const open = findings.filter((f) => f.status === 'open' && !f.accepted);
  const counts = Object.fromEntries(SEV.map((k) => [k, open.filter((f) => f.severity === k).length]));
  const acceptedN = data?.scan?.counts?.accepted ?? findings.filter((f) => f.accepted).length;
  const resolvedN = findings.filter((f) => f.status !== 'open' && !f.accepted).length;
  const last = jobs[0] || null;
  const inFlight = last && ['queued', 'running'].includes(last.status);
  const lastDone = jobs.find((j) => ['done', 'error'].includes(j.status));
  const anomalyOpen = (telemetry?.anomalies || []).filter((a) => a.closedAt == null).length;
  const nextAt = data?.scanSchedule?.nextBaselineAt || null;

  const posture = useMemo(() => {
    if (!data) return '';
    const openN = open.length;
    const bits = [];
    if (inFlight) bits.push(`A ${last.mode === 'active' ? 'active' : 'baseline'} scan is running${last.stage ? ` · ${SCANNER_LABEL[last.stage] || last.stage}` : ''}`);
    else if (!data.assurance && !data.scan?.lastScanAt) bits.push('Assurance is not on for this app');
    else if (!data.scan?.lastScanAt && !lastDone) bits.push('No baseline has run yet');
    else bits.push(openN ? `${openN} open finding${openN === 1 ? '' : 's'}` : 'No open findings');
    if (acceptedN) bits.push(`${acceptedN} accepted as the kit baseline, not defects`);
    if (data.scan?.lastScanAt && !inFlight) bits.push(`last baseline ${ago(data.scan.lastScanAt)}`);
    if (nextAt) bits.push(`next monthly baseline ${new Date(nextAt).toLocaleDateString()}`);
    if (data.runtime?.node) bits.push(data.runtime.behind === true ? `runtime ${data.runtime.node} below the ${data.runtime.floor} floor` : `runtime ${data.runtime.node} at the floor`);
    if (data.image?.ref) bits.push(`image ${imageShort(data.image.ref)}${data.image.scanAt ? ` scanned ${ago(data.image.scanAt)}` : ''}`);
    bits.push(anomalyOpen ? `${anomalyOpen} behavior flag${anomalyOpen === 1 ? '' : 's'} open` : 'behavior baseline holding');
    return bits.join(' · ');
  }, [data, open.length, inFlight, last, acceptedN, nextAt, anomalyOpen, lastDone]);

  const shown = findings.filter((f) => {
    const st = f.accepted ? 'accepted' : (f.status === 'open' ? 'open' : 'resolved');
    if (status !== 'all' && st !== status) return false;
    if (sev !== 'all' && f.severity !== sev) return false;
    if (scanner !== 'all' && f.scanner !== scanner) return false;
    return true;
  });

  if (err) return <p className="cs-help">Security data unavailable: {err}</p>;
  if (!data) return <p className="cs-help">Loading security posture…</p>;

  return (
    <>
      <p className="live-posture">{posture}</p>
      {!data.assurance && (
        <p className="cs-help">Security scanning is the Assurance add-on: a monthly passive baseline (ZAP, Nuclei, TLS) plus browser checks. Findings stay in the ledger until they resolve.</p>
      )}
      <div className="ap-sec-summary">
        {SEV.map((k) => (
          <div key={k} className={`ap-sev is-${k} ${counts[k] ? '' : 'is-zero'}`}><strong>{counts[k]}</strong><span>{k}</span></div>
        ))}
        <div className="ap-sev is-zero"><strong>{acceptedN}</strong><span>accepted</span></div>
        <div className="ap-sev is-zero"><strong>{resolvedN}</strong><span>resolved</span></div>
      </div>
      {isAdmin && data.assurance && (
        <div className="ap-sec-actions">
          <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" disabled={busy || inFlight} onClick={runScan} title="Passive only — reads responses, never sends attack payloads">
            {inFlight ? 'Scan in progress…' : 'Run baseline scan'}
          </button>
          {msg && <span className="cs-help" style={{ margin: 0 }}>{msg}</span>}
        </div>
      )}

      <LiveSection title="Scanners" meta="what has been running">
        <div className="live-scanners">
          {SCANNERS.map((sc) => {
            const job = jobs.find((j) => (j.scanners || []).includes(sc.key)) || null;
            const st = stageOf(job, sc.key);
            const owned = open.filter((f) => f.scanner === sc.key).length;
            if (sc.hourly) {
              return (
                <article key={sc.key} className="live-scanner" data-nova-note="Container image scan (ECR enhanced scanning): what is inside this instance's running image; fixes ride the next kit release">
                  <strong>{sc.name}</strong>
                  <p>{sc.test}</p>
                  <span className="live-scanner-stat">
                    {data.image?.scanAt ? 'read' : 'not read yet'}
                    {owned ? ` · ${owned} open` : (data.image?.scanAt ? ' · clean' : '')}
                  </span>
                  <span className="cs-help live-mono" title={data.image?.ref || ''}>{data.image?.ref ? imageShort(data.image.ref) : (data.image?.kitRef || '')}{data.image?.scanAt ? ` · ${ago(data.image.scanAt)}` : ''}</span>
                </article>
              );
            }
            return (
              <article key={sc.key} className="live-scanner">
                <strong>{sc.name}</strong>
                <p>{sc.test}</p>
                <span className="live-scanner-stat">
                  {st ? `${st.status}${st.status === 'error' ? ' · failed' : ''}` : (job ? 'pending' : 'not run')}
                  {owned ? ` · ${owned} open` : ''}
                </span>
                {job && <span className="cs-help">{job.mode === 'active' ? 'Active' : 'Baseline'} · {ago(job.finishedAt || job.createdAt)}</span>}
              </article>
            );
          })}
        </div>
        {data.runtime && (
          <p className="cs-help" style={{ marginTop: 10 }} data-nova-note="Runtime floor: the Node version this instance phones home vs apps.monitor.minNodeVersion; below the floor means a kit update is due">
            Runtime: {data.runtime.node
              ? <>Node <span className="live-mono">{data.runtime.node}</span>{data.runtime.behind === true ? <span className="live-warn"> — below the {data.runtime.floor} floor, a kit update is due</span> : data.runtime.floor ? ` — at the ${data.runtime.floor} floor` : ''}</>
              : 'this instance does not report its Node version yet (pre-2026-10-26 kit); the next update adds it'}.
            {' '}Image findings refresh hourly from the registry; web scanners run monthly and on demand.
          </p>
        )}
        <div style={{ marginTop: 16 }}>
          <RunChart jobs={jobs.filter((j) => j.summary || j.status === 'done' || j.status === 'error')} />
          <RunLegend />
        </div>
        <div className="cs-plan-tablewrap" style={{ marginTop: 12 }}>
          <table>
            <thead><tr><th>When</th><th>Mode</th><th>Who</th><th>Result</th><th>Kept</th></tr></thead>
            <tbody>
              {jobs.map((j) => {
                const kept = Object.entries(j.summary?.scanners || {}).map(([k, c]) => `${SCANNER_LABEL[k] || k} ${c}`).join(' · ');
                const failed = (j.summary?.errors || []).map((e) => e.scanner || e.error || e).join(', ');
                return (
                  <tr key={j.id}>
                    <td>{new Date(j.createdAt).toLocaleString()}</td>
                    <td>{j.mode === 'active' ? 'active' : 'baseline'}</td>
                    <td>{j.requestedBy === 'schedule' ? 'monthly schedule' : (j.requestedBy || '—')}</td>
                    <td>{j.status}{j.stage && ['queued', 'running'].includes(j.status) ? ` · ${SCANNER_LABEL[j.stage] || j.stage}` : ''}</td>
                    <td>{kept || '—'}{failed ? <span className="cs-help" style={{ display: 'block', margin: 0 }}>{failed}</span> : null}</td>
                  </tr>
                );
              })}
              {!jobs.length && <tr><td colSpan={5} className="cs-help">No scan has been queued.</td></tr>}
            </tbody>
          </table>
        </div>
      </LiveSection>

      <LiveSection title="Findings ledger" meta="open, accepted baseline, resolved">
        <p className="live-accepted">
          Accepted findings are the kit baseline: inline scripts and styles the app bundle needs, and a missing subresource-integrity tag.
          They stay in the ledger so a change is visible, and they are not open defects. A fleet that looks orange from these is not an outage.
        </p>
        <div className="live-filters" role="group" aria-label="Filter findings">
          {[['open', 'Open'], ['accepted', 'Accepted baseline'], ['resolved', 'Resolved'], ['all', 'All']].map(([id, label]) => (
            <button key={id} type="button" className={status === id ? 'is-on' : ''} onClick={() => setStatus(id)}>{label}</button>
          ))}
          <button type="button" className={sev === 'all' ? 'is-on' : ''} onClick={() => setSev('all')}>Any severity</button>
          {SEV.map((k) => (
            <button key={k} type="button" className={sev === k ? 'is-on' : ''} onClick={() => setSev(k)}>{k}</button>
          ))}
          <button type="button" className={scanner === 'all' ? 'is-on' : ''} onClick={() => setScanner('all')}>Any scanner</button>
          {SCANNERS.map((sc) => (
            <button key={sc.key} type="button" className={scanner === sc.key ? 'is-on' : ''} onClick={() => setScanner(sc.key)}>{sc.name}</button>
          ))}
        </div>
        {!shown.length && <p className="cs-help">{status === 'open' ? 'No open findings. Resolved ones stay in the ledger so a regression reopens instead of looking new.' : 'Nothing in this filter.'}</p>}
        {shown.length > 0 && (
          <div className="cs-plan-tablewrap">
            <table>
              <thead><tr><th>Severity</th><th>Finding</th><th>Scanner</th><th>Where</th><th>First</th><th>Last</th><th>State</th></tr></thead>
              <tbody>
                {shown.map((f) => (
                  <tr key={f.id}>
                    <td className={`ap-finding-sev is-${f.severity}`}>{f.severity}</td>
                    <td>{f.name}{f.detail ? <span className="cs-help" style={{ display: 'block', margin: 0 }}>{f.detail}</span> : null}</td>
                    <td>{SCANNER_LABEL[f.scanner] || f.scanner}</td>
                    <td className="live-mono">{f.scanner === 'image' ? imageShort(f.target) : f.target}</td>
                    <td>{f.firstSeenAt ? new Date(f.firstSeenAt).toLocaleDateString() : '—'}</td>
                    <td>{f.lastSeenAt ? ago(f.lastSeenAt) : '—'}</td>
                    <td>{f.accepted ? 'kit baseline' : (f.status === 'open' ? 'open' : 'resolved')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </LiveSection>

      <LiveSection title="Tests" meta="HTTP and browser checks">
        <p className="cs-help" style={{ marginTop: 0 }}>
          These run on the fleet, separate from the scanners. Editing them lives on the <Link to={appLive(orgId, appId, 'health')}>Health page</Link>.
        </p>
        {checks == null && <p className="cs-help">Loading checks…</p>}
        {checks && !checks.length && <p className="cs-help">No feature checks yet.</p>}
        {checks && checks.length > 0 && (
          <div className="cs-plan-tablewrap">
            <table>
              <thead><tr><th>Check</th><th>Kind</th><th>Target</th><th>Last</th><th>Result</th></tr></thead>
              <tbody>
                {checks.map((f) => (
                  <tr key={f.id}>
                    <td>{f.name}{f.enabled === false ? <span className="cs-help"> · off</span> : null}</td>
                    <td>{f.kind || 'http'}</td>
                    <td className="live-mono">{f.path || f.selector || '—'}</td>
                    <td>{f.lastAt ? ago(f.lastAt) : '—'}</td>
                    <td>{f.lastOk == null ? '—' : f.lastOk ? 'pass' : (f.lastError || `fail${f.lastStatus ? ` · ${f.lastStatus}` : ''}`)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </LiveSection>

      <BehaviorBaseline telemetry={telemetry} slug={slug} controls={controls} />
    </>
  );
}
